import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import {
  deleteInventorySupplier,
  saveInventorySupplier,
} from '@/renderer/services/inventorySupplierService';
import { SaveInventorySupplierRequest } from '@/renderer/types/inventory';
import { apiError, apiSuccess } from '@/renderer/util/notify';

export function useSaveInventorySupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SaveInventorySupplierRequest) => {
      const result = await saveInventorySupplier(payload);
      if (!result.success) {
        throw new Error(result.error ?? 'Failed to save inventory supplier');
      }

      return result.data!;
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      apiSuccess(
        payload.id
          ? 'Inventory supplier updated.'
          : 'Inventory supplier created.',
      );
    },
    onError: (error: Error) => {
      apiError(error.message);
    },
  });
}

export function useDeleteInventorySupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      supplierId,
    }: {
      companyId: number;
      supplierId: string;
    }) => {
      const result = await deleteInventorySupplier(companyId, supplierId);
      if (!result.success) {
        throw new Error(result.error ?? 'Failed to delete inventory supplier');
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      apiSuccess('Inventory supplier deleted.');
    },
    onError: (error: Error) => {
      apiError(error.message);
    },
  });
}
